import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react';
import type { AccessMember } from '../components/stream/RestrictedAccessModal';

/**
 * ModalContext - Centralized modal state management for Dashboard
 *
 * Consolidates 20+ modal-related useState hooks into a single reducer pattern.
 * Only one modal can be open at a time (except for nested modals like context item detail).
 */

// All modal types in the Dashboard
export type ModalType =
  | 'commandPalette'
  | 'suggestions'
  | 'inbox'
  | 'expertFinder'
  | 'newStream'
  | 'contextStore'
  | 'userPrefs'
  | 'integrations'
  | 'streamSettings'
  | 'exportModal'
  | 'branchModal'
  | 'branchHistory'
  | 'connectionLost'
  | 'rateLimit'
  | 'contextItemDetail'
  | 'deleteConfirm'
  | 'restrictedAccess'
  | 'attentionPanel'
  | 'botChat';

// Modal data types for modals that need additional context
export interface DeleteConfirmData {
  streamId: string;
  streamName: string;
}

export interface RestrictedAccessData {
  streamId: string;
  streamName: string;
  members: AccessMember[];
}

// Context item can be any shape - using unknown for flexibility
export type ContextItemData = unknown;

// Union type for modal-specific data
export type ModalData = {
  deleteConfirm?: DeleteConfirmData;
  restrictedAccess?: RestrictedAccessData;
  contextItemDetail?: ContextItemData;
};

// State shape
export interface ModalState {
  activeModal: ModalType | null;
  modalData: ModalData;
}

// Action types
export type ModalAction =
  | { type: 'OPEN'; modal: ModalType; data?: Partial<ModalData> }
  | { type: 'CLOSE'; modal?: ModalType }
  | { type: 'TOGGLE'; modal: ModalType; data?: Partial<ModalData> }
  | { type: 'UPDATE_DATA'; data: Partial<ModalData> };

// Initial state
const initialState: ModalState = {
  activeModal: null,
  modalData: {},
};

// Reducer - exported for testing
// eslint-disable-next-line react-refresh/only-export-components
export function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'OPEN':
      return {
        activeModal: action.modal,
        modalData: action.data ? { ...state.modalData, ...action.data } : state.modalData,
      };

    case 'CLOSE':
      // If modal specified, only close if it matches current
      if (action.modal && state.activeModal !== action.modal) {
        return state;
      }
      return {
        ...state,
        activeModal: null,
      };

    case 'TOGGLE':
      if (state.activeModal === action.modal) {
        return { ...state, activeModal: null };
      }
      return {
        activeModal: action.modal,
        modalData: action.data ? { ...state.modalData, ...action.data } : state.modalData,
      };

    case 'UPDATE_DATA':
      return {
        ...state,
        modalData: { ...state.modalData, ...action.data },
      };

    default:
      return state;
  }
}

// Context value interface
interface ModalContextValue {
  state: ModalState;
  dispatch: React.Dispatch<ModalAction>;
  // Convenience methods
  openModal: (modal: ModalType, data?: Partial<ModalData>) => void;
  closeModal: (modal?: ModalType) => void;
  toggleModal: (modal: ModalType, data?: Partial<ModalData>) => void;
  isModalOpen: (modal: ModalType) => boolean;
  updateModalData: (data: Partial<ModalData>) => void;
}

// Create context
const ModalContext = createContext<ModalContextValue | undefined>(undefined);

// Provider props
interface ModalProviderProps {
  children: React.ReactNode;
}

// Provider component
export function ModalProvider({ children }: ModalProviderProps) {
  const [state, dispatch] = useReducer(modalReducer, initialState);

  const openModal = useCallback((modal: ModalType, data?: Partial<ModalData>) => {
    dispatch({ type: 'OPEN', modal, data });
  }, []);

  const closeModal = useCallback((modal?: ModalType) => {
    dispatch({ type: 'CLOSE', modal });
  }, []);

  const toggleModal = useCallback((modal: ModalType, data?: Partial<ModalData>) => {
    dispatch({ type: 'TOGGLE', modal, data });
  }, []);

  const isModalOpen = useCallback((modal: ModalType) => {
    return state.activeModal === modal;
  }, [state.activeModal]);

  const updateModalData = useCallback((data: Partial<ModalData>) => {
    dispatch({ type: 'UPDATE_DATA', data });
  }, []);

  const value = useMemo(
    () => ({
      state,
      dispatch,
      openModal,
      closeModal,
      toggleModal,
      isModalOpen,
      updateModalData,
    }),
    [state, openModal, closeModal, toggleModal, isModalOpen, updateModalData]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
}

// Hook to access modal context
// eslint-disable-next-line react-refresh/only-export-components
export function useModal(): ModalContextValue {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

// Convenience hooks for specific modals
// eslint-disable-next-line react-refresh/only-export-components
export function useDeleteConfirmModal() {
  const { state, openModal, closeModal } = useModal();
  const isOpen = state.activeModal === 'deleteConfirm';
  const data = state.modalData.deleteConfirm;

  const open = useCallback((streamId: string, streamName: string) => {
    openModal('deleteConfirm', { deleteConfirm: { streamId, streamName } });
  }, [openModal]);

  const close = useCallback(() => {
    closeModal('deleteConfirm');
  }, [closeModal]);

  return { isOpen, data, open, close };
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRestrictedAccessModal() {
  const { state, openModal, closeModal, updateModalData } = useModal();
  const isOpen = state.activeModal === 'restrictedAccess';
  const data = state.modalData.restrictedAccess;

  const open = useCallback((streamId: string, streamName: string, members: AccessMember[]) => {
    openModal('restrictedAccess', { restrictedAccess: { streamId, streamName, members } });
  }, [openModal]);

  const close = useCallback(() => {
    closeModal('restrictedAccess');
  }, [closeModal]);

  const updateMembers = useCallback((members: AccessMember[]) => {
    if (data) {
      updateModalData({ restrictedAccess: { ...data, members } });
    }
  }, [data, updateModalData]);

  return { isOpen, data, open, close, updateMembers };
}
